import { Badge } from '@mantine/core';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { FundWithDealRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { TableRowActionOption } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const getFundsTableColumns = (
	tOrganization: TranslateFunction<I18Namespaces.ORGANIZATION>,
	actions?: {
		onViewLinkedDeals?: (fund: FundWithDealRes) => void;
		onEditFund?: (fund: FundWithDealRes) => void;
	}
): ExtendedColumnDef<FundWithDealRes>[] => {
	const columns: ExtendedColumnDef<FundWithDealRes>[] = [
		createTextColumn({
			headerText: tOrganization('funds.table.headers.name'),
			filterText: tOrganization('funds.table.filter.name'),
			columnLabel: tOrganization('funds.table.headers.name'),
			id: 'name',
			accessorFn: (row) => row.deal.name,
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
		}),
		createTextColumn({
			id: 'targetName',
			headerText: tOrganization('funds.table.headers.target_name'),
			filterText: tOrganization('funds.table.filter.target_name'),
			columnLabel: tOrganization('funds.table.headers.target_name'),
			accessorFn: (row) => row.deal.targetName,
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
		}),
		createTextColumn({
			id: 'currency',
			headerText: tOrganization('funds.table.headers.currency'),
			filterText: tOrganization('funds.table.filter.currency'),
			columnLabel: tOrganization('funds.table.headers.currency'),
			accessorFn: (row) => row.deal.currency,
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
		}),
		createTextColumn({
			accessorKey: 'status',
			headerText: tOrganization('funds.table.headers.status'),
			filterText: tOrganization('funds.table.filter.status'),
			columnLabel: tOrganization('funds.table.headers.status'),
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
		}),
		{
			id: 'restricted',
			header: tOrganization('funds.table.headers.access', { defaultValue: 'Access' }),
			enableSorting: false,
			enableGlobalFilter: false,
			size: 120,
			cell: ({ row }) =>
				row.original.isRestricted && row.original.isVisibleThroughDealStackSupportAccess ? (
					<Badge color="red" variant="light" data-testid="fund-restricted-badge">
						{tOrganization('funds.table.restricted', { defaultValue: 'Restricted' })}
					</Badge>
				) : null,
		},
	];

	if (actions?.onViewLinkedDeals || actions?.onEditFund) {
		columns.push({
			id: 'actions',
			header: '',
			enableSorting: false,
			size: 80,
			cell: ({ row }) => {
				const options: TableRowActionOption[] = [];

				if (actions.onViewLinkedDeals) {
					options.push({
						label: tOrganization('funds.actions.view_linked_deals'),
						onClick: () => actions.onViewLinkedDeals?.(row.original),
						icon: 'link-chain',
						dataTestId: TEST_ELEMENT_IDS.TABLE_ROW_ACTION_VIEW,
					});
				}

				if (actions.onEditFund) {
					options.push({
						label: tOrganization('funds.actions.edit'),
						onClick: () => actions.onEditFund?.(row.original),
						icon: 'pencil-edit-line',
						dataTestId: TEST_ELEMENT_IDS.TABLE_ROW_ACTION_EDIT,
					});
				}

				return <TableRowActionsMenu options={options} />;
			},
		});
	}

	return columns;
};
